import React, { useState, useEffect } from 'react';
import { Card, Col, Typography, Form, Spin, message, Flex } from 'antd';
import { Chatbot } from '../../../../../../Components/Chatbot/Chatbot';
import { myAssistantServices } from '../../../../services/myAssistantServices';
import { v4 as uuidv4 } from 'uuid';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Title, Paragraph } = Typography;

interface ProcessFlowAIGeneratorCardProps {
  loading?: boolean;
  onWorkflowGenerated: (workflowJson: string, workflowName: string) => void;
  generatedWorkflow?: string;
}

// Default cloud WebSocket URL
const DEFAULT_WEBSOCKET_URL = process.env.REACT_APP_WS_SHARED_URL;

export const ProcessFlowAIGeneratorCard: React.FC<ProcessFlowAIGeneratorCardProps> = ({
  loading = false,
  onWorkflowGenerated,
  generatedWorkflow
}) => {
  const [name, setName] = useState('');
  const [processFlowGeneratorConfig, setProcessFlowGeneratorConfig] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [chatbotId, setChatbotId] = useState<string | null>(null);
  const channelId = React.useMemo(() => uuidv4(), []);
  // Initialize chatbot: get ID by flowType, then get config
  useEffect(() => {
    const initializeChatbot = async () => {
      try {
        // Get chatbot ID dynamically by flowType
        const dynamicChatbotId = await myAssistantServices.getChatbotIdByFlowType('process');

        if (!dynamicChatbotId) {
          logger.error('[Process Flow Generator] No chatbot found for flowType "process"');
          return;
        }

        // Clear conversation history on reload
        localStorage.removeItem(`chatbot_messages_${dynamicChatbotId}`);
        localStorage.removeItem(`chatbot_greeting_shown_${dynamicChatbotId}`);

        setChatbotId(dynamicChatbotId);

        // Get chatbot configuration
        const responseData = await myAssistantServices.getChatbotConfig(dynamicChatbotId);
        const webchatConfig = responseData.webchatConfig;
        const wsUrlToUse = DEFAULT_WEBSOCKET_URL || webchatConfig.wsUrl || process.env.REACT_APP_WS_URL;

        setProcessFlowGeneratorConfig({
          botName: responseData.name || webchatConfig.botName || 'Process Flow Generator',
          colorMain: webchatConfig.colorMain,
          colorTitle: webchatConfig.colorTitle,
          iconBgColor: webchatConfig.iconBgColor || '#ffffff',
          wsUrl: wsUrlToUse,
        });
      } catch (error) {
        logger.error('[Process Flow Generator] Error initializing chatbot:', error);
      }
    };

    initializeChatbot();
  }, []);

  // Handler for when user clicks Accept button on artifact
  const handleArtifactAccept = async (artifact: { content: string, language?: string }) => {
    try {

      // Parse the JSON to validate it
      const parsedWorkflow = JSON.parse(artifact.content);

      // Validate it's a proper n8n workflow
      if (!parsedWorkflow.nodes || !Array.isArray(parsedWorkflow.nodes)) {
        logger.error('VALIDATION FAILED: nodes check');
        message.error('Invalid workflow format: missing nodes array');
        return;
      }

      if (!parsedWorkflow.connections || typeof parsedWorkflow.connections !== 'object') {
        logger.error('VALIDATION FAILED: connections check');
        message.error('Invalid workflow format: missing connections object');
        return;
      }
      // Extract workflow name from parsed JSON
      const workflowName = parsedWorkflow.name || 'Untitled Workflow';
      // Update state for display (optional, since the workflow name comes from JSON)
      setName(workflowName);

      // The workflow JSON already has the correct name, so just stringify it
      const workflowJson = JSON.stringify(parsedWorkflow, null, 2);
      // Show saving state
      setIsSaving(true);

      // Call the parent callback with the workflow JSON and name
      // The parent will handle saving and showing success/error messages
      await onWorkflowGenerated(workflowJson, workflowName);

    } catch (error: any) {
      message.error('Invalid workflow JSON format. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="processflow-ai-generator-card" style={{ position: 'relative' }}>
      {isSaving && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          borderRadius: '8px',
        }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16, fontSize: 16, fontWeight: 500 }}>
            Saving workflow...
          </Paragraph>
        </div>
      )}
      <Col xs={24}>
        <Title level={4}>Describe Your Process Flow</Title>
        <Paragraph type="secondary">
          Type a short description of the workflow you want to automate. We'll generate an n8n workflow for you using AI.
        </Paragraph>
        <Form layout="vertical">
          {/* <Form.Item label="Workflow Name" required>
            <Input
              placeholder="Enter workflow name..."
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={loading}
            />

          </Form.Item> */}
          {processFlowGeneratorConfig && chatbotId ? (
            <Chatbot
              wsUrl={processFlowGeneratorConfig.wsUrl || DEFAULT_WEBSOCKET_URL}
              chatbotId={chatbotId}
              channelId={channelId}
              urlApi={`${process.env.REACT_APP_API_HOST}/chatbot`}
              isWeb={true}
              isPreview={false}
              primaryColor={processFlowGeneratorConfig.colorTitle}
              headerColor={processFlowGeneratorConfig.colorMain}
              iconColor={processFlowGeneratorConfig.iconBgColor}
              botName={processFlowGeneratorConfig.botName}
              onArtifactAccept={handleArtifactAccept}
              autoAcceptArtifacts={false}
            />
          ) : (
            <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
              <Spin size="large" tip="Loading Process Flow Generator..." />
            </Flex>
          )}
          {/* <Form.Item label="Description / Prompt" required>
            <TextArea
              rows={4}
              placeholder="Describe the process you want to automate..."
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              disabled={loading}
            />
          </Form.Item> */}
          {/* <Space> */}
          {/* <Button
              type="primary"
              icon={loading ? <Spin size="small" /> : <ThunderboltOutlined />}
              onClick={handleGenerate}
              disabled={loading || !prompt.trim() || !name.trim()}
            >
              {loading ? 'Generating...' : 'Generate with AI'}
            </Button> */}
          {/* </Space> */}
        </Form>
        {generatedWorkflow && (
          <Card type="inner" title="Generated Workflow JSON" style={{ marginTop: 16 }}>
            <pre style={{ maxHeight: 300, overflow: 'auto' }}>{generatedWorkflow}</pre>
          </Card>
        )}
      </Col>
    </Card>
  );
};